/**
 * act-as-waap-rules.ts — React Query hooks for B4 WAAP rule surfaces.
 *
 * Covers: AI-WAF Advanced, AI-WAF Compliance, AI-WAF Custom Rules,
 * AI-WAF Web Access Control (WAC), Bot Management, API Protection (DLP).
 *
 * All calls via actAsClient (X-Act-As-Org injected automatically).
 * WAAP responses are NOT enveloped — no .data unwrap; guard null on 400/404.
 */

import { useQuery } from '@tanstack/react-query';
import { actAsClient } from '@/api/act-as-client';
import { actAsWAAPKeys } from '@/api/act-as-waap';

/* ── Shared null-guard helper ────────────────────────────────────────────── */

function isNullStatus(e: unknown): boolean {
  const status = (e as { response?: { status?: number } })?.response?.status;
  return status === 400 || status === 404 || status === 501;
}

/* ─────────────────────────────────────────────────────────────────────────
   AI-WAF Advanced Strategies (B5 spec)
   ────────────────────────────────────────────────────────────────────────── */

/** Advanced strategy type numeric constants (spec: api/waap/advanced-strategy.ts). */
export const ADVANCED_TYPES = {
  ANTI_LEECH:            1,
  BRUTE_FORCE:           2,
  CC_PROTECTION:         4,
  XML_PROTECTION:        5,
  WEAK_PASSWORD:         6,
  VULNERABILITY_SCANNING: 7,
  HONEYPOT:              9,
  COOKIE:                10,
  ILLEGAL_DOWNLOAD:      11,
} as const;

export const ADVANCED_TYPE_LABELS: Record<number, string> = {
  1:  'Anti-Leech',
  2:  'Brute Force Protection',
  4:  'CC Protection',
  5:  'XML Protection',
  6:  'Weak Password',
  7:  'Vulnerability Scanning',
  9:  'Honeypot',
  10: 'Cookie Protection',
  11: 'Illegal Download',
};

export const ADVANCED_TYPE_VALUES = Object.values(ADVANCED_TYPES);

export interface AdvancedStrategyListItem {
  id?: number;
  name: string;
  description?: string;
  type: number;
  sites?: number[];
  config?: Record<string, unknown>;
}

/** GET /waap/domains/:anchorId/advanced-strategies?type=N */
export function useActAsWAAPAdvancedStrategies(
  anchorId: string | undefined,
  type: number,
) {
  return useQuery({
    queryKey: [...actAsWAAPKeys.aspect(anchorId ?? '', 'advanced-strategies'), type] as const,
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<AdvancedStrategyListItem[]> => {
      try {
        const res = await actAsClient.get<AdvancedStrategyListItem[]>(
          `/waap/domains/${anchorId}/advanced-strategies?type=${type}`,
        );
        return res.data ?? [];
      } catch (e) {
        if (isNullStatus(e)) return [];
        throw e;
      }
    },
  });
}

/** GET /waap/domains/:anchorId/advanced-enabled-sites — site picker. */
export function useActAsWAAPAdvancedEnabledSites(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'advanced-enabled-sites'),
    enabled: Boolean(anchorId),
    staleTime: 2 * 60_000,
    retry: false,
    queryFn: async (): Promise<Array<{ id: number; domain: string }>> => {
      try {
        const res = await actAsClient.get<Array<{ id: number; domain: string }>>(
          `/waap/domains/${anchorId}/advanced-enabled-sites`,
        );
        return res.data ?? [];
      } catch {
        return [];
      }
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   AI-WAF Compliance Rules (B6 spec)
   ────────────────────────────────────────────────────────────────────────── */

export interface ComplianceRule {
  id?: number;
  name: string;
  description?: string;
  sites?: number[];
  config?: {
    compliance_type?: string;
    mode?: string;
    [key: string]: unknown;
  };
}

/** GET /waap/domains/:anchorId/compliance-rules */
export function useActAsWAAPComplianceRules(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'compliance-rules'),
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<ComplianceRule[]> => {
      try {
        const res = await actAsClient.get<ComplianceRule[]>(
          `/waap/domains/${anchorId}/compliance-rules`,
        );
        return res.data ?? [];
      } catch (e) {
        if (isNullStatus(e)) return [];
        throw e;
      }
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   AI-WAF Custom Rules (B7 spec) — service-scoped
   ────────────────────────────────────────────────────────────────────────── */

export interface CustomRule {
  id?: number;
  name: string;
  description?: string;
  sites?: number[];
  config?: Record<string, unknown>;
}

/** GET /services/:serviceId/waap/custom-rules */
export function useActAsWAAPCustomRules(serviceId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.customRules(serviceId ?? ''),
    enabled: Boolean(serviceId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<CustomRule[]> => {
      try {
        const res = await actAsClient.get<CustomRule[]>(
          `/services/${serviceId}/waap/custom-rules`,
        );
        return res.data ?? [];
      } catch (e) {
        if (isNullStatus(e)) return [];
        throw e;
      }
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   AI-WAF Web Access Control (B8 spec) — per-site whitelist + auto-blacklist
   ────────────────────────────────────────────────────────────────────────── */

export interface WhitelistRule {
  path: string;
  ruleId?: string;
  remark?: string;
}

/**
 * GET /waap/domains/:anchorId/website-whitelist/:siteId
 * Returns [] on 400/404.
 */
export function useActAsWAAPWebsiteWhitelist(
  anchorId: string | undefined,
  siteId: string | number | undefined,
) {
  const siteKey = String(siteId ?? '');
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', `website-whitelist/${siteKey}`),
    enabled: Boolean(anchorId) && Boolean(siteId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<WhitelistRule[]> => {
      try {
        const res = await actAsClient.get<WhitelistRule[]>(
          `/waap/domains/${anchorId}/website-whitelist/${siteId}`,
        );
        return res.data ?? [];
      } catch (e) {
        if (isNullStatus(e)) return [];
        throw e;
      }
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   Bot Management (B11 spec) — service-scoped
   ────────────────────────────────────────────────────────────────────────── */

export interface BotBrowserInterrogation {
  mode: string;
  sites?: number[];
  [key: string]: unknown;
}

export interface BotRequestWhitelistRule {
  path?: string;
  [key: string]: unknown;
}

export interface BotStrategy {
  id?: number;
  name: string;
  description?: string;
  sites?: number[];
  browser_interrogation?: BotBrowserInterrogation[];
  request_whitelist?: BotRequestWhitelistRule[];
  [key: string]: unknown;
}

/** GET /services/:serviceId/waap/bot-strategies */
export function useActAsWAAPBotStrategies(serviceId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.botStrategies(serviceId ?? ''),
    enabled: Boolean(serviceId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<BotStrategy[]> => {
      try {
        const res = await actAsClient.get<BotStrategy[]>(
          `/services/${serviceId}/waap/bot-strategies`,
        );
        return res.data ?? [];
      } catch (e) {
        if (isNullStatus(e)) return [];
        throw e;
      }
    },
  });
}

/** GET /services/:serviceId/waap/bot-enabled-sites */
export function useActAsWAAPBotEnabledSites(serviceId: string | undefined) {
  return useQuery({
    queryKey: [...actAsWAAPKeys.botStrategies(serviceId ?? ''), 'enabled-sites'] as const,
    enabled: Boolean(serviceId),
    staleTime: 2 * 60_000,
    retry: false,
    queryFn: async (): Promise<Array<{ id: number; domain: string }>> => {
      try {
        const res = await actAsClient.get<Array<{ id: number; domain: string }>>(
          `/services/${serviceId}/waap/bot-enabled-sites`,
        );
        return res.data ?? [];
      } catch {
        return [];
      }
    },
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   API Protection (B10 spec)
   ────────────────────────────────────────────────────────────────────────── */

/** DLP rule config object (spec: WaapDlpRule.config). */
export interface DlpRuleConfig {
  pii_type?: string;
  class?: string;
  pii_level?: number;
  enabled?: boolean;
  req_enabled?: boolean;
  is_pii_masked?: boolean;
  is_pii_resp_block?: boolean;
  mask_all_api?: boolean;
  mask_specified_api?: boolean;
  pre_n?: number;
  last_m?: number;
  whitelist?: string[];
  [key: string]: unknown;
}

export interface DlpRule {
  id?: number;
  name?: string;
  config: DlpRuleConfig;
  [key: string]: unknown;
}

/** GET /waap/domains/:anchorId/api-protection/data-loss/rules */
export function useActAsWAAPDlpRules(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'api-protection/data-loss/rules'),
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<DlpRule[]> => {
      try {
        const res = await actAsClient.get<DlpRule[]>(
          `/waap/domains/${anchorId}/api-protection/data-loss/rules`,
        );
        return res.data ?? [];
      } catch (e) {
        if (isNullStatus(e)) return [];
        throw e;
      }
    },
  });
}

/** GET /waap/domains/:anchorId/api-protection/data-loss/api-asset-options */
export function useActAsWAAPDlpAssetOptions(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'api-protection/data-loss/api-asset-options'),
    enabled: Boolean(anchorId),
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async (): Promise<Record<string, unknown>[]> => {
      try {
        const res = await actAsClient.get<Record<string, unknown>[]>(
          `/waap/domains/${anchorId}/api-protection/data-loss/api-asset-options`,
        );
        return res.data ?? [];
      } catch {
        return [];
      }
    },
  });
}

/** GET /waap/domains/:anchorId/api-protection/overview/charts */
export function useActAsWAAPApiProtectionOverviewCharts(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'api-protection/overview/charts'),
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<{ charts: unknown[] } | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await actAsClient.get<{ charts: unknown[] }>(
          `/waap/domains/${anchorId}/api-protection/overview/charts?tz=${encodeURIComponent(tz)}`,
        );
        return res.data;
      } catch (e) {
        if (isNullStatus(e)) return null;
        throw e;
      }
    },
  });
}

/** GET /waap/domains/:anchorId/api-protection/discovery/rules */
export function useActAsWAAPApiDiscoveryRules(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'api-protection/discovery/rules'),
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<Record<string, unknown>[]> => {
      try {
        const res = await actAsClient.get<Record<string, unknown>[]>(
          `/waap/domains/${anchorId}/api-protection/discovery/rules`,
        );
        return res.data ?? [];
      } catch (e) {
        if (isNullStatus(e)) return [];
        throw e;
      }
    },
  });
}

/** GET /waap/domains/:anchorId/api-protection/vulnerability/overview/charts */
export function useActAsWAAPVulnerabilityCharts(anchorId: string | undefined) {
  return useQuery({
    queryKey: actAsWAAPKeys.aspect(anchorId ?? '', 'api-protection/vulnerability/overview/charts'),
    enabled: Boolean(anchorId),
    staleTime: 60_000,
    retry: false,
    queryFn: async (): Promise<{ charts: unknown[] } | null> => {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const res = await actAsClient.get<{ charts: unknown[] }>(
          `/waap/domains/${anchorId}/api-protection/vulnerability/overview/charts?tz=${encodeURIComponent(tz)}`,
        );
        return res.data;
      } catch (e) {
        if (isNullStatus(e)) return null;
        throw e;
      }
    },
  });
}
